import * as React from "react"
import { Box } from "@mui/material"
import {
  KBarProvider,
  KBarPortal,
  KBarPositioner,
  KBarAnimator,
  KBarSearch,
  KBarResults,
  useMatches,
  useKBar,
  useRegisterActions,
  createAction,
} from "kbar"
import { useRouter } from "next/router"
import copy from "copy-to-clipboard"
import { useQueryClient } from "react-query"

import { useAuth } from "context/auth.js"
import { useCurrentUser } from "context/db"
import { hasPlanFeatures } from "components/PlanGate"
import {
  HiOutlineDuplicate,
  HiOutlineHome,
  HiOutlineKey,
  HiOutlineOfficeBuilding,
  HiOutlineUserCircle,
} from "react-icons/hi"
import { GiAngelWings } from "react-icons/gi"
import { FaTheaterMasks } from "react-icons/fa"
import CompanyLogo from "components/CompanyLogo"

const searchStyle = {
  padding: "24px 16px",
  fontSize: "16px",
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  border: "none",
  backgroundColor: "gray.50",
  color: "gray.700",
  borderBottomColor: "gray.200",
  borderBottomWidth: 1,
  borderBottomStyle: "solid",
}

const animatorStyle = {
  maxWidth: "600px",
  width: "100%",
  background: "white",
  color: "gray.700",
  borderRadius: "8px",
  overflow: "hidden",
  boxShadow: 5,
}

const groupNameStyle = {
  padding: "8px 16px",
  fontSize: "10px",
  textTransform: "uppercase",
  opacity: 0.5,
}

function RenderResults() {
  useImpersonateActions()
  const { results, rootActionId } = useMatches()
  const { data: currentUser } = useCurrentUser()
  useMyCompetitorActions(currentUser)
  useGoToCompanyActions()
  const hasInternalFeatures = hasPlanFeatures({
    user: currentUser,
    allowedFeatures: ["internalTools"],
  })

  return (
    <KBarResults
      items={results.filter((result: any) => {
        if (result.internal) {
          if (hasInternalFeatures) {
            return true
          } else {
            return false
          }
        }
        return true
      })}
      onRender={({ item, active }) =>
        typeof item === "string" ? (
          <Box sx={groupNameStyle}>{item}</Box>
        ) : (
          <ResultItem
            action={item}
            active={active}
            currentRootActionId={rootActionId}
          />
        )
      }
    />
  )
}

// eslint-disable-next-line
const ResultItem = React.forwardRef(
  (
    {
      action,
      active,
      currentRootActionId,
    }: {
      action: any
      active: boolean
      currentRootActionId: any
    },
    ref: React.Ref<HTMLDivElement>
  ) => {
    const ancestors = React.useMemo(() => {
      if (!currentRootActionId) return action.ancestors
      const index = action.ancestors.findIndex(
        // @ts-ignore
        ancestor => ancestor.id === currentRootActionId
      )
      // +1 removes the currentRootAction; e.g.
      // if we are on the "Set theme" parent action,
      // the UI should not display "Set theme… > Dark"
      // but rather just "Dark"
      return action.ancestors.slice(index + 1)
    }, [action.ancestors, currentRootActionId])

    return (
      <Box
        ref={ref}
        sx={{
          padding: "12px 16px",
          backgroundColor: active ? "gray.50" : "transparent",
          borderLeftColor: active ? "gray.400" : "transparent",
          borderLeftWidth: `2px`,
          borderLeftStyle: `solid`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            fontSize: 14,
          }}
        >
          {action.icon && action.icon}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div>
              {ancestors.length > 0 &&
                ancestors.map((ancestor: any) => (
                  <React.Fragment key={ancestor.id}>
                    <span
                      style={{
                        opacity: 0.5,
                        marginRight: 8,
                      }}
                    >
                      {ancestor.name}
                    </span>
                    <span
                      style={{
                        marginRight: 8,
                      }}
                    >
                      &rsaquo;
                    </span>
                  </React.Fragment>
                ))}
              <span>{action.name}</span>
            </div>
            {action.subtitle && (
              <span style={{ fontSize: 12 }}>{action.subtitle}</span>
            )}
          </div>
        </div>
        {action.shortcut?.length ? (
          <div
            aria-hidden
            style={{ display: "grid", gridAutoFlow: "column", gap: "4px" }}
          >
            {action.shortcut.map((sc: any) => (
              <kbd
                key={sc}
                style={{
                  padding: "2px 6px",
                  background: "rgba(0 0 0 / .1)",
                  borderRadius: "4px",
                  fontSize: 12,
                }}
              >
                {sc}
              </kbd>
            ))}
          </div>
        ) : null}
      </Box>
    )
  }
)

export default function CommandBarProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const router = useRouter()

  const actions = [
    {
      id: "home",
      name: "Home",
      icon: <HiOutlineHome />,
      keywords: "dashboard home",
      section: "Navigation",
      perform: () => router.push("/dashboard"),
    },
    {
      id: "account",
      name: "My Account",
      icon: <HiOutlineUserCircle />,
      shortcut: ["m", "a"],
      keywords: "my account profile",
      section: "Navigation",
      perform: () => router.push("/dashboard/account"),
    },
    {
      id: "luz-internal",
      name: "Internal Tools",
      icon: <HiOutlineKey />,
      shortcut: ["i", "t"],
      keywords: "internal tools",
      section: "Navigation",
      internal: true,
      perform: () => router.push("/dashboard/internal"),
    },
    {
      id: "flappy-luz",
      name: "Flappy Luz",
      icon: <GiAngelWings />,
      shortcut: ["flappy"],
      keywords: "flappy luz",
      internal: true,
      perform: () => router.push("https://flappy-luz.gillkyle.repl.co/"),
    },
    {
      id: "copy-url",
      name: "Copy Current URL",
      icon: <HiOutlineDuplicate />,
      keywords: "copy url",
      section: "Utilities",
      perform: () => copy(window.location.href),
    },
  ]

  return (
    <KBarProvider actions={actions}>
      <KBarPortal>
        <KBarPositioner>
          <Box component={KBarAnimator} sx={animatorStyle}>
            <Box component={KBarSearch} sx={searchStyle} />
            <RenderResults />
          </Box>
        </KBarPositioner>
      </KBarPortal>
      {children}
    </KBarProvider>
  )
}

const impersonateStart = createAction({
  name: "Enter User to impersonate",
  icon: <FaTheaterMasks />,
  keywords: "impersonate user",
  section: "Utilities",
  // @ts-ignore
  internal: true,
})

function useImpersonateActions() {
  const { search } = useKBar(state => ({
    search: state.searchQuery,
  }))
  const queryClient = useQueryClient()
  const { setImpersonation } = useAuth()

  useRegisterActions(
    [
      impersonateStart,
      createAction({
        name: "User ID: " + search,
        icon: <FaTheaterMasks />,
        section: "Utilities",
        parent: impersonateStart.id,
        perform: () => {
          queryClient.invalidateQueries()
          setImpersonation({
            id: search,
            email: "someone@email.com",
          })
        },
        // @ts-ignore
        internal: true,
      }),
    ],
    [search]
  )
}

const opsCompanySearch = createAction({
  name: "Ops Company Tools",
  icon: <HiOutlineOfficeBuilding />,
  keywords: "company",
  section: "Utilities",
  // @ts-ignore
  internal: true,
})

function useGoToCompanyActions() {
  const router = useRouter()
  const { search } = useKBar(state => ({
    search: state.searchQuery,
  }))

  useRegisterActions(
    [
      opsCompanySearch,
      createAction({
        name: "Company ID: " + search,
        icon: <HiOutlineOfficeBuilding />,
        section: "Utilities",
        parent: opsCompanySearch.id,
        perform: () => {
          router.push(`/dashboard/internal/ops/details/groups/${search}`)
        },
        // @ts-ignore
        internal: true,
      }),
    ],
    [search]
  )
}

function useMyCompetitorActions(user: any) {
  const router = useRouter()
  const userComps = user?.group?.purchased_companies
  const competitorActions =
    userComps?.map((comp: any) => {
      return createAction({
        name: comp.name,
        icon: (
          <CompanyLogo
            customStyle={{ maxHeight: 18, maxWidth: 18 }}
            url={comp.domain}
          />
        ),
        section: "My Competitors",
        perform: () => router.push(`/dashboard/companies/${comp.id}/overview`),
      })
    }) ?? []
  useRegisterActions([...competitorActions], [userComps])
}
